import React from 'react'
import { Box, InputLabel, Typography, type SxProps, type Theme } from '@mui/material'

/** Shared helper for type → unit pickers. */
export const ACADEMIC_UNIT_COMBINED_HELPER =
  'First select an academic unit type, then select an academic unit.'

export interface AcademicUnitTypeAndUnitRowProps {
  /** Label shown once above both controls (e.g. "Academic Unit"). */
  groupLabel?: string
  /** Shown under `groupLabel` (e.g. {@link ACADEMIC_UNIT_COMBINED_HELPER}). */
  helperText?: string
  /** When true, shows the required asterisk on `groupLabel`. */
  groupRequired?: boolean
  /** ~1/3 width — academic unit type control */
  typeControl: React.ReactNode
  /** ~2/3 width — scoped academic unit control */
  unitControl: React.ReactNode
  gap?: number
  sx?: SxProps<Theme>
}

/**
 * Two-column row for “unit type” then “unit” per design (flex 1/3 + 2/3).
 */
export const AcademicUnitTypeAndUnitRow: React.FC<AcademicUnitTypeAndUnitRowProps> = ({
  groupLabel,
  helperText,
  groupRequired = false,
  typeControl,
  unitControl,
  gap = 2,
  sx
}) => (
  <Box sx={{ width: '100%', ...sx }}>
    {(groupLabel != null || helperText != null) && (
      <Box>
        {groupLabel != null && (
          <InputLabel required={groupRequired} sx={{ marginBottom: helperText != null ? 0.5 : 2 }}>
            {groupLabel}
          </InputLabel>
        )}
        {helperText != null && (
          <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0, mb: 2 }}>
            {helperText}
          </Typography>
        )}
      </Box>
    )}
    <Box
      display="flex"
      flexDirection={{ xs: 'column', sm: 'row' }}
      alignItems="flex-start"
      gap={gap}
      sx={{ width: '100%' }}
    >
      <Box sx={{ flex: { xs: 'none', sm: '0 0 33%' }, width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}>
        {typeControl}
      </Box>
      <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}>
        {unitControl}
      </Box>
    </Box>
  </Box>
)

export default AcademicUnitTypeAndUnitRow
